import React, { useEffect, useState, useCallback } from 'react';
import Pagination from '../components/Pagination';
import { usePagination } from '../hooks/usePagination';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useParams } from 'react-router-dom';
import { getRepository, grantAccess, revokeAccess, createAccessRequest, getAllUsers } from '../services/api';
import { formatAccessExpiry } from '../utils/formatAccessExpiry';
import './RepositoryDetail.css';

function serviceDeskTicketError(value) {
  const ticket = String(value || '').trim().replace(/^#/, '');
  if (!ticket) return 'Enter the service desk request number.';
  if (!/^(?:[A-Fa-f0-9]{6}|[A-Fa-f0-9]{24})$/.test(ticket)) {
    return 'Enter the service desk ticket code from the email, such as A1B2C3, or the full request id.';
  }
  return '';
}

const RepositoryDetail = () => {
  const { owner, repo } = useParams();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [repository, setRepository] = useState(null);
  const [loadingRepo, setLoadingRepo] = useState(true);
  const [showGrantForm, setShowGrantForm] = useState(false);
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [portalUsers, setPortalUsers] = useState([]);
  const [usePortalUser, setUsePortalUser] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    userId: '',
    permission: 'read',
    reason: '',
    serviceDeskRequestNumber: '',
    expiresAt: ''
  });
  const [message, setMessage] = useState({ type: '', text: '' });

  const fetchPortalUsers = useCallback(async () => {
    try {
      const response = await getAllUsers();
      setPortalUsers(response.data);
    } catch (error) {
      console.error('Error fetching portal users:', error);
    }
  }, []);

  const fetchRepository = useCallback(async () => {
    try {
      setLoadingRepo(true);
      const response = await getRepository(owner, repo);
      setRepository(response.data);
    } catch (error) {
      console.error('Error fetching repository:', error);
      setMessage({ type: 'error', text: 'Failed to load repository' });
    } finally {
      setLoadingRepo(false);
    }
  }, [owner, repo]);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/login');
      return;
    }

    if (user) {
      fetchRepository();
      if (user.role === 'admin') {
        fetchPortalUsers();
      }
      // Auto-fill GitHub username for access requests
      if (user.githubUsername) {
        setFormData(prev => ({ ...prev, username: user.githubUsername }));
      } else if (user.accountType === 'github') {
        setFormData(prev => ({ ...prev, username: user.username }));
      }
    }
  }, [user, loading, navigate, fetchRepository, fetchPortalUsers]);

  const handleGrantAccess = async (e) => {
    e.preventDefault();
    try {
      // If using portal user, use their email/username, otherwise use the GitHub username directly
      const usernameToUse = usePortalUser && formData.userId 
        ? portalUsers.find(u => u._id === formData.userId)?.email || formData.username
        : formData.username;
      
      await grantAccess(
        repository._id,
        usernameToUse,
        formData.permission,
        formData.expiresAt || undefined
      );
      setMessage({ type: 'success', text: `Access granted successfully` });
      setFormData({ username: '', userId: '', permission: 'push', reason: '', expiresAt: '' });
      setUsePortalUser(false);
      setShowGrantForm(false);
      fetchRepository();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Failed to grant access' 
      });
    }
  };

  const handleRevokeAccess = async (username) => {
    if (!window.confirm(`Are you sure you want to revoke access from ${username}?`)) {
      return;
    }

    try {
      await revokeAccess(repository._id, username);
      setMessage({ type: 'success', text: `Access revoked from ${username}` });
      fetchRepository();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Failed to revoke access' 
      });
    }
  };

  const handleCreateRequest = async (e) => {
    e.preventDefault();
    const ticketError = serviceDeskTicketError(formData.serviceDeskRequestNumber);
    if (ticketError) {
      setMessage({ type: 'error', text: ticketError });
      return;
    }
    try {
      await createAccessRequest(
        repository._id,
        formData.username,
        formData.permission,
        formData.reason,
        formData.serviceDeskRequestNumber
      );
      setMessage({ type: 'success', text: 'Access request created successfully' });
      setFormData({ username: '', userId: '', permission: 'read', reason: '', serviceDeskRequestNumber: '', expiresAt: '' });
      setShowRequestForm(false);
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.error || 'Failed to create request' 
      });
    }
  };

  const collabPages = usePagination(repository?.collaborators || [], 15, repository?._id);

  if (loading || loadingRepo) {
    return (
      <div className="repository-detail">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!repository) {
    return (
      <div className="repository-detail">
        <div className="alert alert-error">Repository not found</div>
      </div>
    );
  }

  // Admins can always manage, otherwise check repository permissions
  const canManage = user?.role === 'admin' || repository.canManage;

  return (
    <div className="repository-detail">
      <div className="repo-detail-header">
        <button 
          className="btn btn-secondary"
          onClick={() => navigate('/repositories')}
        >
          ← Back to Repositories
        </button>
        <div>
          <h1 className="page-title">
            <a 
              href={repository.url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="repo-link"
            >
              {repository.fullName}
            </a>
            {user?.role === 'admin' && (
              <span className="admin-badge">👑 Admin Access</span>
            )}
          </h1>
          {repository.description && (
            <p className="repo-description">{repository.description}</p>
          )}
          {repository.accessExpiresAt && (
            <p className="repo-description">Your access expires {formatAccessExpiry(repository.accessExpiresAt)}</p>
          )}
        </div>
      </div>

      {message.text && (
        <div className={`alert alert-${message.type}`}>
          {message.text}
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h2 className="card-title">Collaborators</h2>
          {canManage && (
            <div className="card-actions">
              <button
                className="btn btn-primary"
                onClick={() => {
                  setShowGrantForm(!showGrantForm);
                  setShowRequestForm(false);
                }}
              >
                Grant Access
              </button>
            </div>
          )}
        </div>

        {showGrantForm && canManage && (
          <form onSubmit={handleGrantAccess} className="access-form">
            {user?.role === 'admin' && (
              <div className="form-group">
                <label className="form-label">
                  <input
                    type="checkbox"
                    checked={usePortalUser}
                    onChange={(e) => {
                      setUsePortalUser(e.target.checked);
                      setFormData({ ...formData, username: '', userId: '' });
                    }}
                    style={{ marginRight: '8px' }}
                  />
                  Grant access to portal user
                </label>
              </div>
            )}
            
            {usePortalUser && user?.role === 'admin' ? (
              <div className="form-group">
                <label className="form-label">Select Portal User</label>
                <select
                  className="form-select"
                  value={formData.userId}
                  onChange={(e) => {
                    const selectedUser = portalUsers.find(u => u._id === e.target.value);
                    setFormData({ 
                      ...formData, 
                      userId: e.target.value,
                      username: selectedUser?.githubUsername || selectedUser?.username || ''
                    });
                  }}
                  required
                >
                  <option value="">Select a user...</option>
                  {portalUsers
                    .filter(u => u.githubUsername || u.accountType === 'github')
                    .map(u => (
                      <option key={u._id} value={u._id}>
                        {u.displayName || u.username} ({u.githubUsername || u.username}) - {u.email}
                      </option>
                    ))}
                </select>
                {formData.userId && !portalUsers.find(u => u._id === formData.userId)?.githubUsername && (
                  <p className="form-hint" style={{ color: '#bc4c00', marginTop: '8px' }}>
                    ⚠️ This user needs a GitHub username set. Go to User Management to set it.
                  </p>
                )}
              </div>
            ) : (
              <div className="form-group">
                <label className="form-label">GitHub Username or Portal User Email</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  placeholder="username or email"
                />
                <p className="form-hint">Enter GitHub username or portal user's email address</p>
              </div>
            )}
            
            <div className="form-group">
              <label className="form-label">Permission</label>
              <select
                className="form-select"
                value={formData.permission}
                onChange={(e) => setFormData({ ...formData, permission: e.target.value })}
              >
                <option value="pull">Read</option>
                <option value="push">Write</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Access expires (optional)</label>
              <input
                type="date"
                className="form-input"
                value={formData.expiresAt}
                onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                min={new Date().toISOString().slice(0, 10)}
              />
              <p className="form-hint">Leave empty for permanent access. Expired access is revoked automatically.</p>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">Grant Access</button>
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={() => {
                  setShowGrantForm(false);
                  setUsePortalUser(false);
                  setFormData({ username: '', userId: '', permission: 'push', reason: '', expiresAt: '' });
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        <div className="collaborators-list">
          {repository.collaborators && repository.collaborators.length > 0 ? (
            collabPages.pageItems.map((collab, index) => (
              <div key={index} className="collaborator-item">
                <div className="collaborator-info">
                  <img 
                    src={collab.avatarUrl} 
                    alt={collab.username}
                    className="collaborator-avatar"
                  />
                  <div>
                    <div className="collaborator-name">{collab.username}</div>
                    {collab.accessExpiresAt && (
                      <div className="collaborator-expiry">Expires {formatAccessExpiry(collab.accessExpiresAt)}</div>
                    )}
                    <div className="collaborator-permissions">
                      {collab.permissions?.admin && <span className="permission-badge admin">Admin</span>}
                      {collab.permissions?.push && !collab.permissions?.admin && <span className="permission-badge write">Write</span>}
                      {collab.permissions?.pull && !collab.permissions?.push && <span className="permission-badge read">Read</span>}
                    </div>
                  </div>
                </div>
                {canManage && collab.username !== user.username && (
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => handleRevokeAccess(collab.username)}
                  >
                    Revoke
                  </button>
                )}
              </div>
            ))
          ) : (
            <p className="empty-message">No collaborators found</p>
          )}
          <Pagination
            page={collabPages.page}
            totalPages={collabPages.totalPages}
            total={collabPages.total}
            pageSize={collabPages.pageSize}
            onPageChange={collabPages.setPage}
            label="collaborators"
          />
        </div>
      </div>

      {!canManage && (
        <div className="card">
          <div className="card-header">
            <h2 className="card-title">Request Access</h2>
          </div>
          <p className="request-access-intro">
            Request access with a business justification. A manager, repo owner, or portal admin will review it;
            once approved, you receive GitHub collaborator access.
          </p>
          {!showRequestForm ? (
            <button
              className="btn btn-primary"
              onClick={() => setShowRequestForm(true)}
            >
              Request Access
            </button>
          ) : (
            <form onSubmit={handleCreateRequest} className="access-form">
              <div className="form-group">
                <label className="form-label">GitHub username</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  required
                  placeholder={user?.githubUsername || user?.username || "your GitHub username"}
                />
                <p className="form-hint">
                  {user?.githubUsername || user?.username
                    ? "Requesting for yourself (you can change if requesting for someone else)."
                    : "Enter your GitHub username. Set it in Settings so it's saved for next time."}
                </p>
              </div>
              <div className="form-group">
                <label className="form-label">Permission</label>
                <select
                  className="form-select"
                  value={formData.permission}
                  onChange={(e) => setFormData({ ...formData, permission: e.target.value })}
                >
                  <option value="read">Read</option>
                  <option value="write">Write</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Service desk request number</label>
                <input
                  type="text"
                  className="form-input"
                  value={formData.serviceDeskRequestNumber}
                  onChange={(e) => setFormData({ ...formData, serviceDeskRequestNumber: e.target.value })}
                  placeholder="e.g. A1B2C3"
                  pattern="(#?[A-Fa-f0-9]{6}|[A-Fa-f0-9]{24})"
                  required
                />
                <p className="form-hint">The 6-character ticket code from the service desk email, or the full request id. The same ticket can be used to request more than one repository.</p>
              </div>
              <div className="form-group">
                <label className="form-label">Business justification (required)</label>
                <textarea
                  className="form-input"
                  value={formData.reason}
                  onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
                  rows="3"
                  placeholder="Explain the business need for this repository access (min. 10 characters)"
                  required
                  minLength={10}
                />
                <p className="form-hint">Approvers will review this justification before granting access.</p>
              </div>
              <div className="form-actions">
                <button type="submit" className="btn btn-primary">Submit Request</button>
                <button 
                  type="button" 
                  className="btn btn-secondary"
                  onClick={() => setShowRequestForm(false)}
                >
                  Cancel
                </button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
};

export default RepositoryDetail;
